import type { ApiClient } from './client';

export type WebhookEventType =
  | 'message.new'
  | 'conversation.assigned'
  | 'conversation.closed';

export const WEBHOOK_EVENT_TYPES: WebhookEventType[] = [
  'message.new',
  'conversation.assigned',
  'conversation.closed',
];

export interface WebhookEndpoint {
  id: string;
  siteId: string | null;
  name: string;
  url: string;
  events: WebhookEventType[];
  status: 'active' | 'paused' | 'deleted';
  createdAt: string;
  /** Сервер отдаёт ТОЛЬКО при создании. На list/update — undefined. */
  secret?: string;
}

interface WebhookRaw {
  id: string;
  site_id: string | null;
  name: string;
  url: string;
  events: string[];
  status: string;
  created_at: string;
  secret?: string;
}

function toWebhook(r: WebhookRaw): WebhookEndpoint {
  const e: WebhookEndpoint = {
    id: r.id,
    siteId: r.site_id,
    name: r.name,
    url: r.url,
    events: r.events as WebhookEventType[],
    status: r.status as WebhookEndpoint['status'],
    createdAt: r.created_at,
  };
  if (r.secret) e.secret = r.secret;
  return e;
}

export async function listWebhooks(client: ApiClient): Promise<WebhookEndpoint[]> {
  const raw = await client.request<{ webhooks: WebhookRaw[] }>('/api/v1/webhooks');
  return raw.webhooks.map(toWebhook);
}

export interface CreateWebhookInput {
  name: string;
  url: string;
  events: WebhookEventType[];
  siteId?: string;
}

export async function createWebhook(
  client: ApiClient,
  input: CreateWebhookInput,
): Promise<WebhookEndpoint> {
  const json: Record<string, unknown> = {
    name: input.name,
    url: input.url,
    events: input.events,
  };
  if (input.siteId !== undefined) json.site_id = input.siteId;
  const raw = await client.request<WebhookRaw>('/api/v1/webhooks', {
    method: 'POST',
    json,
  });
  return toWebhook(raw);
}

export interface UpdateWebhookInput {
  name?: string;
  url?: string;
  events?: WebhookEventType[];
  status?: 'active' | 'paused';
}

export async function updateWebhook(
  client: ApiClient,
  id: string,
  input: UpdateWebhookInput,
): Promise<WebhookEndpoint> {
  const raw = await client.request<WebhookRaw>(`/api/v1/webhooks/${id}`, {
    method: 'PATCH',
    json: input,
  });
  return toWebhook(raw);
}

export async function deleteWebhook(client: ApiClient, id: string): Promise<void> {
  await client.request<void>(`/api/v1/webhooks/${id}`, { method: 'DELETE' });
}
